import { WASH_CREDIT_ENTRY_TYPE_LABELS, type WashCreditEntry } from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Lock, LockOpen, SlidersHorizontal, Timer } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AdjustCreditDialog, CreditLockDialog } from "../../../../features/wash/credit-dialogs.js";
import { useWashCredit, useWashPermissions, useWashUnits } from "../../../../features/wash/queries.js";
import {
  CreditStatusBadge,
  Field,
  TableFrame,
  Th,
  washCrumb,
  WashPage,
} from "../../../../features/wash/shared.js";
import { creditLabel, formatDuration, signedDuration } from "../../../../features/wash/wash-values.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({ station: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/_app/yikama/krediler/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CreditRoute,
});

const ENTRY_TONE: Record<WashCreditEntry["entryType"], "success" | "info" | "warning" | "neutral"> = {
  PURCHASE: "success",
  USAGE: "info",
  ADJUST: "warning",
  EXPIRE: "neutral",
};

/**
 * Self-servis kredisi ayrıntısı: bakiye, bitiş, durum ve append-only defter (satın alma / kullanım / düzeltme / süre sonu;
 * en yeni 500 satır). Kod GÖSTERİLMEZ (bearer; sunucu yanıtında da yoktur). Düzeltme ve kilit `wash.manage`.
 */
function CreditRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const permissions = useWashPermissions();
  const credit = useWashCredit(id);
  const units = useWashUnits(credit.data?.stationId ?? null);
  const [dialog, setDialog] = useState<"adjust" | "lock" | null>(null);
  const label = credit.data
    ? creditLabel(credit.data, t("wash.credits.codeCredit"))
    : t("wash.credits.detailTitle");
  const unitName = (unitId: string | null) =>
    unitId ? (units.data?.find((unit) => unit.id === unitId)?.name ?? t("wash.cycles.unknownUnit")) : "—";

  return (
    <WashPage
      title={t("wash.credits.detailHeading", { label })}
      breadcrumb={[
        washCrumb(
          t("menu.washCredits"),
          "/yikama/krediler",
          search.station ? { station: search.station } : {},
        ),
        { label },
      ]}
      actions={
        credit.data && permissions.canManage ? (
          <>
            <Button variant="outline" onClick={() => setDialog("adjust")}>
              <SlidersHorizontal aria-hidden="true" />
              {t("wash.credits.adjust")}
            </Button>
            {credit.data.status !== "EXPIRED" ? (
              <Button variant="outline" onClick={() => setDialog("lock")}>
                {credit.data.status === "BLOCKED" ? (
                  <LockOpen aria-hidden="true" />
                ) : (
                  <Lock aria-hidden="true" />
                )}
                {credit.data.status === "BLOCKED" ? t("wash.credits.unblock") : t("wash.credits.block")}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {credit.error ? (
        <Alert tone="danger">{errorMessage(credit.error)}</Alert>
      ) : !credit.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <>
          <Card className="p-4">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="credit-summary">
              <Field label={t("wash.credits.balance")}>
                <span
                  className={`tabular text-xl ${credit.data.balanceSeconds.startsWith("-") ? "text-destructive" : ""}`}
                >
                  {formatDuration(credit.data.balanceSeconds)}
                </span>
              </Field>
              <Field label={t("common.status")}>
                <CreditStatusBadge status={credit.data.status} />
              </Field>
              <Field label={t("wash.credits.expiresAt")}>{formatDateTime(credit.data.expiresAt)}</Field>
              <Field label={t("wash.fields.station")}>{credit.data.stationName}</Field>
              <Field label={t("wash.credits.card")}>
                <span className="font-mono">{credit.data.cardNoMasked ?? t("wash.credits.noCard")}</span>
              </Field>
              <Field label={t("wash.credits.createdAt")}>{formatDateTime(credit.data.createdAt)}</Field>
            </dl>
            {credit.data.balanceSeconds.startsWith("-") ? (
              <Alert tone="warning" className="mt-4">
                {t("wash.credits.negativeHint")}
              </Alert>
            ) : null}
            <p className="mt-3 text-xs text-muted-foreground">{t("wash.credits.codeNotice")}</p>
          </Card>

          <Card className="grid gap-3 p-4">
            <h2 className="text-base font-semibold">{t("wash.credits.ledger")}</h2>
            {credit.data.entries.length === 0 ? (
              <EmptyState
                headingLevel={3}
                icon={<Timer />}
                title={t("wash.credits.noEntries")}
                description={t("wash.credits.noEntriesDescription")}
              />
            ) : (
              <TableFrame label={t("wash.credits.ledger")}>
                <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                  <tr>
                    <Th>{t("wash.credits.occurredAt")}</Th>
                    <Th>{t("wash.credits.entryType")}</Th>
                    <Th align="right">{t("wash.credits.seconds")}</Th>
                    <Th>{t("wash.fields.unit")}</Th>
                    <Th>{t("wash.credits.source")}</Th>
                    <Th>{t("wash.fields.reason")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {credit.data.entries.map((entry) => (
                    <tr key={entry.id} className="border-t">
                      <td className="px-3 py-2 tabular whitespace-nowrap">
                        {formatDateTime(entry.occurredAt)}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge tone={ENTRY_TONE[entry.entryType]}>
                          {WASH_CREDIT_ENTRY_TYPE_LABELS[entry.entryType]}
                        </StatusBadge>
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular whitespace-nowrap ${
                          entry.seconds.startsWith("-") ? "text-destructive" : ""
                        }`}
                      >
                        {signedDuration(entry.seconds)}
                      </td>
                      <td className="px-3 py-2">{unitName(entry.unitId)}</td>
                      <td className="px-3 py-2 text-xs">
                        {entry.posSaleId ? (
                          <Link
                            to="/kasa/satislar/$id"
                            params={{ id: entry.posSaleId }}
                            className="underline underline-offset-4"
                          >
                            {t("wash.activations.openSale")}
                            {entry.posLineNo !== null
                              ? ` · ${t("wash.activations.line", { no: entry.posLineNo })}`
                              : ""}
                          </Link>
                        ) : entry.cycleId ? (
                          t("wash.credits.fromCycle")
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">{entry.reason ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </TableFrame>
            )}
          </Card>

          {dialog === "adjust" ? (
            <AdjustCreditDialog open onOpenChange={(open) => !open && setDialog(null)} credit={credit.data} />
          ) : null}
          {dialog === "lock" ? (
            <CreditLockDialog open onOpenChange={(open) => !open && setDialog(null)} credit={credit.data} />
          ) : null}
        </>
      )}
    </WashPage>
  );
}
